// src/pages/SalesReturnRecord/components/SalesReturnTable.js
import React, { useEffect, useState } from "react";
import { Table, Button, Tooltip, Popconfirm, Grid } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  setIsEditSalesReturn,
  setSelectedSalesReturnRecord,
} from "../../SalesReturn/salesReturnSlice";
import { fetchSalesReturnWithDetails } from "../../SalesReturn/salesReturnThunks";
import { accessChecker, formatToCompanyTimezone, numberFormatTax } from "../../../lib/functions";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { useIsAdminUser } from "../../../lib/accessList";
import { useTxLock } from "../../../hooks/useTxLock";
import {
  InfoCircleOutlined,
  InboxOutlined,
  ApartmentOutlined
} from "@ant-design/icons";

import {
  receiveSalesReturnInventory,
  unpackSalesReturn,
  fetchSalesReturnRecord
} from "../SalesReturnRecordThunk";

import {
  Modal,
  Descriptions
} from "antd";

import Swal from "sweetalert2";
import VoucherPdfActions from "../../../components/common/VoucherPdfActions";

const SalesReturnRecordTable = ({ printMode = false, handleDelete }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, filters } = useSelector(
    (state) => state.salesReturnRecord,
  );
  const authInfo = useSelector(selectAuthInfo);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [tatmeenModal, setTatmeenModal] = useState({
    open: false,
    record: null,
  });



  const canReceive =
    tatmeenModal.record?.tatmeen_sscc_status === "not_started";

  const canUnpack =
    tatmeenModal.record?.tatmeen_sscc_status === "shipped";
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGstColumns = isIndia && isGstEnabled;
  const showTax = !isIndia || isGstEnabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  const money = (v) => Number(v || 0).toFixed(2);
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();
  const isMobile = !screens.md;
  // const [isAdminUser, isAdminUserSet] = useState(false);

  // useEffect(() => {
  //     const hrRoles = [
  //       // Admin role
  //       "ForthERP_Basic_admin",
  //       "ForthERP_Standard_admin",
  //       "ForthERP_Pro_admin",
  //       "PharmaERP_Basic_admin",
  //       "PharmaERP_Standard_admin",
  //       "PharmaERP_Pro_admin",
  //       "PharmaBooks_Basic_admin",
  //       "PharmaBooks_Standard_admin",
  //       "PharmaBooks_Pro_admin",
  //       "ForthBooks_Basic_admin",
  //       "ForthBooks_Standard_admin",
  //       "ForthBooks_Pro_admin",
  //       "HRPayroll_Standard_admin",
  //       "HRPayroll_Pro_admin",
  //       "SuperERP_admin",
  //       "SuperERP_super_admin",
  //     ];

  //     const userRoles = authInfo.userInfo.user_role_names
  //       ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
  //       : [];

  //     const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

  //     isAdminUserSet(isAdminHR);
  //   }, [authInfo]);

  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const showTatmeenColumn =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');
  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  const unpackStatusColor = {
    sent: "#52c41a",   // green  → success
    pending: "#1677ff",   // blue   → in progress
    failed: "#ff4d4f",   // red    → failure
    not_started: "#8c8c8c",   // gray   → waiting
  };

  const unpackStatusLabel = {
    sent: "Unpacked",
    pending: "Unpacking…",
    failed: "Failed",
    not_started: "Not Unpacked",
  };

  // ✅ Reusable renderer for unpack status
  const renderUnpackStatus = (status, record) => {
    // No unpack log entry exists yet
    if (!record.unpack_log_id) {
      if (record.sscc_code) {
        return <span style={{ color: "#faad14" }}>Not Started</span>;
      }
      return <span style={{ color: "#bfbfbf" }}>—</span>;
    }

    return (
      <span
        style={{
          color: unpackStatusColor[status] || "#000",
          fontWeight: 500,
        }}
      >
        {unpackStatusLabel[status] || "-"}
      </span>
    );
  };

  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60, fixed: !isMobile && "left" },
    { title: "Voucher No", dataIndex: "vch_no", fixed: !isMobile && "left" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer/Debtor", dataIndex: "customer" },
    {
      title: "Sub Total",
      dataIndex: "subtotal",
      width: 110,
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      width: 100,
      render: (val) => Number(val).toFixed(2),
    },
    showTax && {
      title: taxLabel,
      dataIndex: "vat",
      width: 90,
      render: (val) => Number(val).toFixed(2),
    },
    showGstColumns && {
      title: "CGST",
      dataIndex: "cgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "SGST",
      dataIndex: "sgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "IGST",
      dataIndex: "igst_amount",
      render: numberFormatTax,
      align: "right",
    },
    {
      title: "Service",
      dataIndex: "service",
      render: (val) => Number(val).toFixed(2),
      width: 120,
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
      width: 120,
    },
    hasTatmeenEntryAccess && showTatmeenColumn && {
      title: "Tatmeen Status",
      dataIndex: "tatmeen_sent_status",
      width: 130,
      render: (status) => {
        const colorMap = {
          sent: "#52c41a",
          pending: "#faad14",
          failed: "#ff4d4f",
          cancelled: "#8c8c8c",
          not_started: "#8c8c8c",
        };
        return (
          <span
            style={{
              color: colorMap[status] || "#000",
              fontWeight: 500,
            }}
          >
            {status || "-"}
          </span>
        );
      },
    },
    hasTatmeenEntryAccess && showTatmeenColumn && {
      title: "Unpack Status",
      dataIndex: "unpack_status",
      width: 130,
      render: renderUnpackStatus,
    },
    // {
    //   title: "Paid",
    //   dataIndex: "paid",
    //   render: (val) => Number(val).toFixed(2),
    // },
    // {
    //   title: "Due",
    //   dataIndex: "due",
    //   render: (val) => Number(val).toFixed(2),
    // },
    !printMode && {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: !isMobile && "right",
      align: "center",
      render: (_, record) => {
        const txLocked = isTxLocked(record?.date);
        return (
          <div className="action-icons">
            {/* QUICK VOUCHER VIEW */}
            <Tooltip title="View Sales Return Voucher">
              <Button

                type="link"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(
                    `/sales/quick-sales-return-voucher/${record.sale_r_id}`,
                  )
                }
              />
            </Tooltip>

            {hasTatmeenEntryAccess && showTatmeenColumn && (
              <Tooltip title="Tatmeen Details">
                <Button
                  type="link"
                  icon={<InfoCircleOutlined />}
                  onClick={() =>
                    setTatmeenModal({
                      open: true,
                      record,
                    })
                  }
                />
              </Tooltip>
            )}

            {/* EDIT */}
            <Tooltip title={
              txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Edit"
            }>
              <Button
                type="link"
                disabled={!(isAdminUser || canEdit) || txLocked}
                icon={<EditOutlined />}
                onClick={() => navigate(`/sales-return/edit/${record.sale_r_id}`)}
              />
            </Tooltip>

            {/* DELETE */}

            <Tooltip title={
              txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Delete"
            }>
              {/* <Popconfirm
                  title="Are you sure you want to delete this sale Return?"
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  onConfirm={handleDelete} // ✅ delete only here
                  // onCancel={(e) => e?.stopPropagation()}
                >
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    // onClick={() => handleDelete(record.sale_r_id)}
                  />
                </Popconfirm> */}
              <Button
                type="link"
                disabled={!(isAdminUser || canDelete) || txLocked}
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.sale_r_id)}
              />
            </Tooltip>

            <VoucherPdfActions
              url={`/print/sales-return/${record.sale_r_id}`}
              fileName={`SalesReturn_${record.vch_no || record.sale_r_id}.pdf`}
            />

          </div>
        );
      },
    },
  ].filter(Boolean);

  const handleReceiveInventory = async () => {

    const result = await Swal.fire({
      title: "Receive returned items?",
      text: tatmeenModal.record.vch_no,
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {

      await dispatch(
        receiveSalesReturnInventory(
          tatmeenModal.record.sale_r_id
        )
      ).unwrap();

      Swal.fire(
        "Success",
        "Items received successfully.",
        "success"
      );

      dispatch(
        fetchSalesReturnRecord({
          selectedFilterType: filters.filterType,
          selectedRecordType: filters.recordType,
          filters
        })
      );

    } catch (err) {

      const message =
        typeof err === "string"
          ? err
          : err?.message
            ? err.message
            : err?.response?.data?.message
              ? err.response.data.message
              : "Receive failed";

      Swal.fire("Failed", message, "error");

    }

  };

  const handleUnpack = async () => {

    const result = await Swal.fire({
      title: "Unpack returned SSCC?",
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {

      await dispatch(
        unpackSalesReturn(
          tatmeenModal.record.sale_r_id
        )
      ).unwrap();

      Swal.fire(
        "Success",
        "SSCC unpacked successfully.",
        "success"
      );

      dispatch(
        fetchSalesReturnRecord({
          selectedFilterType: filters.filterType,
          selectedRecordType: filters.recordType,
          filters
        })
      );

    } catch (err) {

      const message =
        typeof err === "string"
          ? err
          : err?.message
            ? err.message
            : err?.response?.data?.message
              ? err.response.data.message
              : "Unpack failed";

      Swal.fire("Failed", message, "error");

    }

  };

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60, fixed: !isMobile && "left" },
    { title: "VCH", dataIndex: "vch_no", fixed: !isMobile && "left" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer/Debtor", dataIndex: "customer" },
    { title: "Employee", dataIndex: "employee" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    { title: "Rate", dataIndex: "rate" },
    { title: "Discount%", render: money, dataIndex: "discount" },
    { title: "VAT%", render: money, dataIndex: "vat" },
    { title: "Qty", dataIndex: "qty" },
    { title: "Total", dataIndex: "total" },
    hasTatmeenEntryAccess && showTatmeenColumn && {
      title: "Unpack Status",
      dataIndex: "unpack_status",
      width: 130,
      render: renderUnpackStatus,
    },
    !printMode && {
      title: "Actions",
      className: "no-print",
      fixed: !isMobile && "right",
      width: 110,
      render: (_, record) => (
        <div className="action-icons">
          {/* QUICK VOUCHER VIEW */}
          <Tooltip title="View Sales Return Voucher">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/sales-return/view/${record.sale_r_id}`)}
            />
          </Tooltip>

          {/* EDIT */}
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/sales-return/edit/${record.sale_r_id}`)}
            />
          </Tooltip>

          {/* DELETE */}
          {isAdminUser && (
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.sale_r_id)}
              />
            </Tooltip>
          )}

          <VoucherPdfActions
            url={`/print/sales-return/${record.sale_r_id}`}
            fileName={`SalesReturn_${record.vch_no || record.sale_r_id}.pdf`}
          />
        </div>
      ),
    },
  ].filter(Boolean);

  // -----------------------
  // Columns for "By Item" filter
  // -----------------------
  const byItemFamily = ["By Item", "By Group", "By Category"];
  const byItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60, fixed: !isMobile && "left" },
    { title: "Voucher No", dataIndex: "vch_no", fixed: !isMobile && "left" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Customer/Debtor", dataIndex: "customer" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    {
      title: "Rate",
      dataIndex: "rate",
      render: (v) => Number(v).toFixed(2),
    },
    { title: "Qty", dataIndex: "qty" },
    { title: "Group", dataIndex: "grpname" },
    { title: "Category", dataIndex: "categoryName" },

    {
      title: "Discount%",
      dataIndex: "discount",
      render: (v) => Number(v).toFixed(2),
    },
    showTax && {
      title: "VAT%",
      dataIndex: "vat",
      render: (v) => Number(v).toFixed(2),
    },
    // {
    //   title: "Service",
    //   dataIndex: "service",
    //   render: (v) => Number(v).toFixed(2),
    // },
    {
      title: "Total",
      dataIndex: "total",
      render: (v) => Number(v).toFixed(2),
    },
    hasTatmeenEntryAccess && showTatmeenColumn && {
      title: "Unpack Status",
      dataIndex: "unpack_status",
      width: 130,
      render: renderUnpackStatus,
    },
    !printMode && {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <div className="action-icons">
          <Tooltip title="View">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(
                  `/sales/quick-sales-return-voucher/${record.sale_r_id}`,
                )
              }
            />
          </Tooltip>

          {isAdminUser && (
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.sale_r_id)}
              />
            </Tooltip>
          )}

          <VoucherPdfActions
            url={`/print/sales-return/${record.sale_r_id}`}
            fileName={`SalesReturn_${record.vch_no || record.sale_r_id}.pdf`}
          />
        </div>
      ),
    },
  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  const totals = list.reduce(
    (acc, row) => {
      if (filters.recordType === "with item") {
        acc.masterQty += Number(row.qty || 0);
        acc.baseQty += Number(row.baseQty || 0); // we’ll enrich in thunk
        acc.total += Number(row.total || 0);
      } else if (byItemFamily.includes(filters.filterType)) {
        acc.total += Number(row.total || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.cgst += Number(row.cgst_amount || 0);
        acc.sgst += Number(row.sgst_amount || 0);
        acc.igst += Number(row.igst_amount || 0);
        acc.service += Number(row.service || 0);
        return acc;
      } else {
        acc.subtotal += Number(row.subtotal || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.cgst += Number(row.cgst_amount || 0);
        acc.sgst += Number(row.sgst_amount || 0);
        acc.igst += Number(row.igst_amount || 0);
        acc.service += Number(row.service || 0);
        acc.total += Number(row.total || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
      }
      return acc;
    },
    {
      masterQty: 0,
      baseQty: 0,
      subtotal: 0,
      discount: 0,
      vat: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      service: 0,
      total: 0,
      paid: 0,
      due: 0,
    },
  );

  // -----------------------
  // Expandable sub-row for WITH ITEM
  // -----------------------

  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  // -----------------------
  // Columns
  // -----------------------   
  const columns =
    filters.recordType === "with item"
      ? withItemColumns
      : byItemFamily.includes(filters.filterType)
        ? byItemColumns
        : summaryColumns;

  if (printMode) {
    return (
      <table className="ledger-print-table">
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "25%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>

        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>Voucher No</td>
            <td>Date</td>
            <td>Customer / Debtor</td>
            <td style={{ textAlign: "right" }}>Sub Total</td>
            <td style={{ textAlign: "right" }}>Discount</td>
            <td style={{ textAlign: "right" }}>{taxLabel}</td>
            <td style={{ textAlign: "right" }}>Service</td>
            <td style={{ textAlign: "right" }}>Total</td>
          </tr>

          {list.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>

              <td style={{
                maxWidth: "150px",
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}>{row.vch_no}</td>

              <td>
                {row.date
                  ? formatToCompanyTimezone(row.date).split(" ")[0]
                  : "-"}
              </td>

              <td
                style={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {row.customer}
              </td>

              <td style={{ textAlign: "right" }}>
                {Number(row.subtotal || 0).toFixed(2)}
              </td>

              <td style={{ textAlign: "right" }}>
                {Number(row.discount || 0).toFixed(2)}
              </td>

              <td style={{ textAlign: "right" }}>
                {Number(row.vat || 0).toFixed(2)}
              </td>

              <td style={{ textAlign: "right" }}>
                {Number(row.service || 0).toFixed(2)}
              </td>

              <td style={{ textAlign: "right" }}>
                {Number(row.total || 0).toFixed(2)}
              </td>
            </tr>
          ))}

          <tr className="ledger-total-row">
            <td colSpan={4} style={{ textAlign: "right" }}>
              Grand Total
            </td>

            <td style={{ textAlign: "right" }}>
              {totals.subtotal.toFixed(2)}
            </td>

            <td style={{ textAlign: "right" }}>
              {totals.discount.toFixed(2)}
            </td>

            <td style={{ textAlign: "right" }}>
              {totals.vat.toFixed(2)}
            </td>

            <td style={{ textAlign: "right" }}>
              {totals.service.toFixed(2)}
            </td>

            <td style={{ textAlign: "right" }}>
              {totals.total.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        bordered
        scroll={printMode ? undefined : { x: columns.reduce((sum, col) => sum + (col.width || 150), 0), y: "65vh" }}
        expandable={expandable}
        size={printMode ? "small" : "middle"}
        summary={() => {
          if (filters.recordType === "with item") {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={11}>
                  <strong>
                    Grand Total : Master U. Qty = {totals.masterQty}, Base U. Qty = {totals.baseQty}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11}>
                  {totals.total.toFixed(2)}
                </Table.Summary.Cell>

                {hasTatmeenEntryAccess && showTatmeenColumn && (
                  <Table.Summary.Cell index={12} />
                )}

                {!printMode && (
                  <Table.Summary.Cell index={hasTatmeenEntryAccess && showTatmeenColumn ? 13 : 12} />
                )}
              </Table.Summary.Row>
            );
          } else if (filters.filterType === "By Item") {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2} >
                  <strong>Grand Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} >
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} colSpan={1} align="left">
                  {totals.discount.toFixed(2)}
                </Table.Summary.Cell>
                {showTax && (
                  <Table.Summary.Cell index={10}>
                    {totals.vat.toFixed(2)}
                  </Table.Summary.Cell>
                )}
                {/* <Table.Summary.Cell index={8}>{totals.service.toFixed(2)}</Table.Summary.Cell> */}
                <Table.Summary.Cell index={showTax ? 11 : 10}>
                  {totals.total.toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
            // Replace the summary's else block (the default "without item" summary):
          } else {
            const taxColOffset = (showTax ? 1 : 0) + (showGstColumns ? 3 : 0);
            const serviceIndex = 6 + taxColOffset;
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>Grand Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />  {/* Date */}
                <Table.Summary.Cell index={3} />  {/* Customer/Debtor */}
                <Table.Summary.Cell index={4}>   {/* Sub Total */}
                  {totals.subtotal.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>   {/* Discount */}
                  {totals.discount.toFixed(2)}
                </Table.Summary.Cell>

                {showTax && (
                  <Table.Summary.Cell index={6}>   {/* VAT */}
                    {totals.vat.toFixed(2)}
                  </Table.Summary.Cell>
                )}

                {showGstColumns && (
                  <Table.Summary.Cell index={7}>
                    {numberFormatTax(totals.cgst || 0)}
                  </Table.Summary.Cell>
                )}
                {showGstColumns && (
                  <Table.Summary.Cell index={8}>
                    {numberFormatTax(totals.sgst || 0)}
                  </Table.Summary.Cell>
                )}
                {showGstColumns && (
                  <Table.Summary.Cell index={9}>
                    {numberFormatTax(totals.igst || 0)}
                  </Table.Summary.Cell>
                )}

                <Table.Summary.Cell index={serviceIndex}>  {/* Service */}
                  {totals.service.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={serviceIndex + 1}>  {/* Total */}
                  {totals.total.toFixed(2)}
                </Table.Summary.Cell>

                {hasTatmeenEntryAccess && showTatmeenColumn && (
                  <>
                    <Table.Summary.Cell index={serviceIndex + 2} />
                    <Table.Summary.Cell index={serviceIndex + 3} />
                  </>
                )}

                {!printMode && (
                  <Table.Summary.Cell index={serviceIndex + 4} />
                )}
              </Table.Summary.Row>
            );
          }
        }}
      />

      <Modal
        title="Tatmeen Details"
        open={tatmeenModal.open}
        onCancel={() =>
          setTatmeenModal({
            open: false,
            record: null,
          })
        }
        footer={
          hasTatmeenEntryAccess
            ? [

              <Button
                key="receive"
                type="primary"
                icon={<InboxOutlined />}
                onClick={handleReceiveInventory}
              >
                Receive Inventory
              </Button>
              ,
              <Button
                key="unpack"
                icon={<ApartmentOutlined />}
                onClick={handleUnpack}
                disabled={tatmeenModal.record?.unpack_status === "pending"}
                loading={tatmeenModal.record?.unpack_status === "pending"}
              >
                Unpack
              </Button>

            ].filter(Boolean)
            : null
        }
      >
        <Descriptions bordered column={1}>

          <Descriptions.Item label="SSCC">
            {tatmeenModal.record?.sscc_code || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Tatmeen Status">
            {tatmeenModal.record?.tatmeen_sent_status || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="SSCC Status">
            {tatmeenModal.record?.tatmeen_sscc_status || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Message ID">
            {tatmeenModal.record?.tatmeen_message_id || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Retry Count">
            {tatmeenModal.record?.tatmeen_retry_count || 0}
          </Descriptions.Item>

          <Descriptions.Item label="Failure Reason">
            {tatmeenModal.record?.tatmeen_failure_reason ? (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#fafafa",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #f0f0f0",
                }}
              >
                {tatmeenModal.record.tatmeen_failure_reason}
              </div>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Unpack Status">
            {tatmeenModal.record?.unpack_status || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Unpacked At">
            {tatmeenModal.record?.unpacked_at
              ? formatToCompanyTimezone(tatmeenModal.record.unpacked_at)
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Unpack Retry Count">
            {tatmeenModal.record?.unpack_retry_count || 0}
          </Descriptions.Item>

          <Descriptions.Item label="Unpack Failure Reason">
            {tatmeenModal.record?.unpack_failure_reason ? (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#fafafa",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #f0f0f0",
                }}
              >
                {tatmeenModal.record.unpack_failure_reason}
              </div>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Unpack Manual Action">
            {tatmeenModal.record?.unpack_manual_action_required || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Unpack Message ID">
            {tatmeenModal.record?.unpack_message_id || "-"}
          </Descriptions.Item>

        </Descriptions>

      </Modal>
    </>

  );
};

export default SalesReturnRecordTable;
